import { boundsOfPoints, type Vec2 } from '../../geom';
import { pickInBox, pickNode } from '../../scene';
import type { Marquee, ToolContext, ToolEvent } from '../types';
import { BaseTool } from './base';

/** Clicks and box selection follow the raw pointer, since nothing is being placed. */
export class SelectTool extends BaseTool {
  private drag: { readonly origin: Vec2; readonly box: Marquee | null } | null = null;

  status(): string {
    return 'Click to select, drag to box-select';
  }

  inProgress(): boolean {
    return this.drag !== null;
  }

  override placingPoint(): boolean {
    return false;
  }

  override pointerDown(ctx: ToolContext, event: ToolEvent): void {
    if (event.button !== 0) return;
    const hit = pickNode(ctx.drawing.scene, event.raw, ctx.tolerance.pick);
    if (hit) {
      if (event.shift) ctx.toggleSelected(hit);
      else ctx.select([hit]);
      return;
    }
    if (!event.shift) ctx.select([]);
    this.drag = { origin: event.raw, box: null };
  }

  override pointerMove(ctx: ToolContext, event: ToolEvent): void {
    if (!this.drag) {
      ctx.hoverEntity(pickNode(ctx.drawing.scene, event.raw, ctx.tolerance.pick));
      return;
    }
    const box = {
      start: this.drag.origin,
      end: event.raw,
      crossing: event.raw.x < this.drag.origin.x,
    };
    this.drag = { ...this.drag, box };
    ctx.showMarquee(box);
  }

  override pointerUp(ctx: ToolContext, event: ToolEvent): void {
    const box = this.drag?.box;
    this.drag = null;
    ctx.showMarquee(null);
    if (!box) return;
    const hits = pickInBox(ctx.drawing.scene, boundsOfPoints([box.start, box.end]), box.crossing);
    ctx.select(event.shift ? [...new Set([...ctx.selection, ...hits])] : hits);
  }

  override cancel(ctx: ToolContext): void {
    this.drag = null;
    ctx.showMarquee(null);
  }
}
